import { API_BASE_URL } from './client';
import { consumeChatNdjsonStream } from './streamNdjson';

export const createAssistant = async (keycloak, payload) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }

  const response = await fetch(`${API_BASE_URL}/assistants/`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create assistant');
  }

  return await response.json();
};

export const deleteAssistant = async (keycloak, assistantId) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/assistants/${assistantId}/`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete assistant');
  }
  return await response.json();
};

export const getAssistantStatus = async (keycloak, assistantId) => {
    if (!keycloak || !keycloak.token) {
        throw new Error('Keycloak not initialized or no token available');
    }

    const response = await fetch(`${API_BASE_URL}/assistants/${assistantId}/status/`, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${keycloak.token}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to get assistant status');
    }

    return await response.json();
};

export const getAssistants = async (keycloak, showAll = false) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }

  const params = new URLSearchParams();
  if (showAll) {
    params.append('show_all', 'true');
  }

  const response = await fetch(`${API_BASE_URL}/assistants/?${params.toString()}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch assistants');
  }

  return await response.json();
};

export const getAssistantEndpoints = async (keycloak, assistantId) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }

  const response = await fetch(`${API_BASE_URL}/assistants/${assistantId}/endpoints/`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch assistant endpoints');
  }

  return await response.json();
};

export const runAssistant = async (keycloak, assistantId) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }

  const response = await fetch(`${API_BASE_URL}/assistants/${assistantId}/run/`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to run assistant');
  }

  return await response.json();
};

export const stopAssistant = async (keycloak, assistantId) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }

  const response = await fetch(`${API_BASE_URL}/assistants/${assistantId}/stop/`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to stop assistant');
  }

  return await response.json();
};


export const sendChatMessage = async (keycloak, assistantId, messages) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }

  const response = await fetch(`${API_BASE_URL}/assistants/${assistantId}/chat/`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      messages,
      max_tokens: 512,
      temperature: 0.7,
      top_p: 0.9
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    // Handle both string and object error details
    let errorDetail = error.detail || 'Failed to send chat message';
    if (typeof errorDetail === 'object' && errorDetail.message) {
      // Create error with both friendly message and full error
      const err = new Error(errorDetail.message);
      err.fullError = errorDetail.full_error || errorDetail.message;
      throw err;
    }
    throw new Error(errorDetail);
  }

  return await response.json();
};

/**
 * Streaming chat (NDJSON). Calls onDelta for each token chunk, onDone when finished, onError on failure.
 */
export const sendChatMessageStream = async (
  keycloak,
  assistantId,
  messages,
  {
    onDelta,
    onDone,
    onPipelineTrace,
    onModuleUi,
    onRouterStatus,
    onUiPart,
    sessionId,
    moduleSessionId,
    moduleActionId,
    uiActionId,
    uiActionPayload,
    uiPartId,
    resumePendingTool,
  } = {},
) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }

  const response = await fetch(`${API_BASE_URL}/assistants/${assistantId}/chat/`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      messages,
      max_tokens: 512,
      temperature: 0.7,
      top_p: 0.9,
      stream: true,
      session_id: sessionId || undefined,
      module_session_id: moduleSessionId || undefined,
      module_action_id: moduleActionId || undefined,
      ui_action_id: uiActionId || undefined,
      ui_action_payload: uiActionPayload || undefined,
      ui_part_id: uiPartId || undefined,
      resume_pending_tool: Boolean(resumePendingTool),
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    let errorDetail = error.detail || 'Failed to send chat message';
    if (typeof errorDetail === 'object' && errorDetail.message) {
      const err = new Error(errorDetail.message);
      err.fullError = errorDetail.full_error || errorDetail.message;
      throw err;
    }
    throw new Error(typeof errorDetail === 'string' ? errorDetail : 'Failed to send chat message');
  }

  /** onDone(doneObj)  doneObj includes model and optional pipeline_trace; onPipelineTrace may fire earlier */
  await consumeChatNdjsonStream(response, { onDelta, onDone, onPipelineTrace, onModuleUi, onRouterStatus, onUiPart });
};

export const duplicateAssistant = async (keycloak, assistantId) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/assistants/${assistantId}/duplicate/`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to duplicate assistant');
  }
  return response.json();
};

export const getAssistantTeam = async (keycloak, assistantId) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/assistants/${assistantId}/team`, {
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to load team');
  }
  return response.json();
};

export const saveAssistantTeam = async (keycloak, assistantId, payload) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/assistants/${assistantId}/team`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const detail = error.detail;
    throw new Error(typeof detail === 'string' ? detail : 'Failed to save team');
  }
  return response.json();
};

export const updateAssistant = async (keycloak, assistantId, payload) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/assistants/${assistantId}/`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to update assistant');
  }
  return response.json();
};

export const uploadAssistantAvatar = async (keycloak, assistantId, file) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const form = new FormData();
  form.append('file', file);
  const response = await fetch(`${API_BASE_URL}/assistants/${assistantId}/avatar/`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
    },
    body: form,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to upload avatar');
  }
  return response.json();
};

export async function fetchAssistant(keycloak, assistantId) {
    if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }

  const res = await fetch(`${API_BASE_URL}/assistants/${assistantId}/`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${keycloak.token}`,
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || res.statusText);
  }
  return res.json();
}
